import React, { useState, useEffect } from 'react';
import styles from './QuickAction.module.css';
import { db } from '../services/firebase';
import { collection, addDoc, doc, updateDoc, serverTimestamp, getDocs, deleteDoc, query, where } from 'firebase/firestore';

function getToday() {
  return new Date().toISOString().slice(0, 10);
}
function getPastDates(days) {
  const dates = [];
  for (let i = 0; i < days; i++) {
    const d = new Date();
    d.setDate(d.getDate() - i);
    dates.push(d.toISOString().slice(0, 10));
  }
  return dates;
}

export default function QuickAction({ item, branch, onClose }) {
  const [action, setAction] = useState('');
  const [quantity, setQuantity] = useState(1);
  const [showScanner, setShowScanner] = useState(!item);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [success, setSuccess] = useState(null);
  const [logs, setLogs] = useState([]);
  const [undoInfo, setUndoInfo] = useState(null);
  const [undoTimeout, setUndoTimeout] = useState(null);

  // Fetch recent logs for this item (last 7 days)
  useEffect(() => {
    if (!item) return;
    let isMounted = true;
    async function fetchLogs() {
      let allLogs = [];
      const dates = getPastDates(7);
      for (const date of dates) {
        const colRef = collection(db, `inventoryLogs/${branch}/${date}`);
        const q = query(colRef, where('itemId', '==', item.id));
        const snap = await getDocs(q);
        allLogs = allLogs.concat(snap.docs.map(doc => ({ id: doc.id, ...doc.data(), date })));
      }
      // Sort by timestamp descending
      allLogs.sort((a, b) => (b.timestamp?.seconds || 0) - (a.timestamp?.seconds || 0));
      if (isMounted) setLogs(allLogs);
    }
    fetchLogs();
    return () => { isMounted = false; };
  }, [item, branch]);

  // Undo handler
  const handleUndo = async () => {
    if (!undoInfo) return;
    setLoading(true);
    setError(null);
    try {
      // Remove log
      const logRef = doc(db, `inventoryLogs/${branch}/${undoInfo.date}/${undoInfo.logId}`);
      await deleteDoc(logRef);
      // Revert stock
      await updateDoc(doc(db, 'inventoryItems', item.id), { on_hand: undoInfo.prevStock });
      setSuccess('Transaction undone.');
      setUndoInfo(null);
      setLogs(logs => logs.filter(l => l.id !== undoInfo.logId));
    } catch (err) {
      setError('Failed to undo transaction.');
    }
    setLoading(false);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!action || !quantity || !item) return;
    setLoading(true);
    setError(null);
    setSuccess(null);
    try {
      // Log transaction
      const date = getToday();
      const logDoc = await addDoc(collection(db, `inventoryLogs/${branch}/${date}`), {
        itemId: item.id,
        itemName: item.name,
        action,
        quantity: parseFloat(quantity),
        timestamp: serverTimestamp(),
        unit: item.unit,
        category: item.category,
        branch,
      });
      // Update stock
      let newStock = item.on_hand;
      if (action === 'receive') newStock += parseFloat(quantity);
      if (action === 'use') newStock -= parseFloat(quantity);
      if (action === 'adjust') newStock = parseFloat(quantity);
      await updateDoc(doc(db, 'inventoryItems', item.id), { on_hand: newStock });
      setSuccess('Transaction saved!');
      // Add to logs immediately
      setLogs(logs => [{
        id: logDoc.id,
        itemId: item.id,
        itemName: item.name,
        action,
        quantity: parseFloat(quantity),
        timestamp: { seconds: Math.floor(Date.now() / 1000) },
        unit: item.unit,
        category: item.category,
        branch,
        date
      }, ...logs]);
      // Set undo info
      setUndoInfo({ logId: logDoc.id, prevStock: item.on_hand, date });
      // Auto-hide undo after 7 seconds
      if (undoTimeout) clearTimeout(undoTimeout);
      const timeout = setTimeout(() => setUndoInfo(null), 7000);
      setUndoTimeout(timeout);
      setLoading(false);
      // Optionally close modal after save, or keep open for undo
      // onClose();
    } catch (err) {
      setError('Failed to save transaction.');
      setLoading(false);
    }
  };

  // Clean up undo timeout on unmount
  useEffect(() => {
    return () => { if (undoTimeout) clearTimeout(undoTimeout); };
  }, [undoTimeout]);

  return (
    <div className={styles.modal}>
      <div className={styles.content}>
        <button className={styles.close} onClick={onClose} disabled={loading}>×</button>
        {showScanner ? (
          <div className={styles.scannerStub}>
            <div className={styles.scannerBox}>[Barcode Scanner Stub]</div>
            <button onClick={() => setShowScanner(false)} className={styles.manualBtn}>Enter manually</button>
          </div>
        ) : (
          <form onSubmit={handleSubmit}>
            <h2>{item ? item.name.toUpperCase() : 'New Transaction'}</h2>
            {item && <div className={styles.itemInfo}>Current stock: {isNaN(item.on_hand) || item.on_hand === null ? '0' : item.on_hand} {item.unit}</div>}
            <div className={styles.actions}>
              <button type="button" className={action === 'receive' ? styles.active : ''} onClick={() => setAction('receive')}>Receive</button>
              <button type="button" className={action === 'use' ? styles.active : ''} onClick={() => setAction('use')}>Use</button>
              <button type="button" className={action === 'adjust' ? styles.active : ''} onClick={() => setAction('adjust')}>Adjust</button>
            </div>
            <div className={styles.field}>
              <label>Quantity</label>
              <input type="number" min="0.1" step="0.1" value={quantity} onChange={e => setQuantity(e.target.value)} required disabled={loading} />
            </div>
            {error && <div className={styles.error}>{error}</div>}
            {success && <div className={styles.success}>{success}</div>}
            <div className={styles.modalActions}>
              <button type="submit" className={styles.save} disabled={!action || loading}>{loading ? 'Saving...' : 'Save'}</button>
              <button type="button" className={styles.cancel} onClick={onClose} disabled={loading}>Cancel</button>
            </div>
          </form>
        )}
        {/* Transaction History */}
        {item && logs.length > 0 && (
          <div className={styles.history}>
            <h3>Recent Transactions</h3>
            <ul>
              {logs.slice(0, 7).map(log => (
                <li key={log.id}>
                  <span className={styles.logDate}>{log.date}</span>
                  <span className={styles.logAction}>{log.action}</span>
                  <span className={styles.logQty}>{log.quantity} {log.unit}</span>
                </li>
              ))}
            </ul>
          </div>
        )}
        {/* Undo Snackbar */}
        {undoInfo && (
          <div className={styles.snackbar}>
            Transaction saved! <button onClick={handleUndo} disabled={loading}>Undo</button>
          </div>
        )}
      </div>
    </div>
  );
} 